import React, { useState, useEffect, useRef } from 'react';
import { X, Send, Trash2, Mic, BotMessageSquare, Loader2, Paperclip } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useChatContext } from '@/context/chat-context';
import { sendChatMessageStream, getChatbotContext, clearChatbotContext, sendAudioMessage, getChatbotHealth, submitChatbotFeedback, uploadChatbotFile, getChatbotFiles, deleteChatbotFile, CHATBOT_FILE_LIMITS } from '@/api/chatbot';
import { ChatMessage, ChatbotContextResponse, ChatbotHealthResponse, FeedbackType, ChatbotUploadedFile } from '@/types/chatbot';
import { useAuth } from '@/stores/authStore';
import { cn } from '@/lib/utils';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { AudioRecorder } from '@/components/audio-recorder';
import { FeedbackButtons } from './feedback-buttons';
import { FeedbackDetailsDialog } from './feedback-details-dialog';
import { FileAttachment, FileUploadProgress } from './file-attachment';
import { useTranslation } from 'react-i18next';


interface FloatingChatProps {
  className?: string;
}

export const FloatingChat: React.FC<FloatingChatProps> = ({ className }) => {
  const { isOpen, closeChat } = useChatContext();
  const { user } = useAuth();
  const { t, i18n } = useTranslation();

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showAudioRecorder, setShowAudioRecorder] = useState(false);
  const [healthData, setHealthData] = useState<ChatbotHealthResponse | null>(null);
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null);
  const [currentEventSource, setCurrentEventSource] = useState<{ abort: () => void } | null>(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState<{ messageId: string; feedbackType: FeedbackType } | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // File upload state
  const [uploadedFiles, setUploadedFiles] = useState<ChatbotUploadedFile[]>([]);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [uploadingFileName, setUploadingFileName] = useState<string | null>(null);
  const [deletingFileId, setDeletingFileId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);


  const isSuperAdmin = Boolean((user as any)?.is_super_admin) || user?.role === 'super_admin' || user?.role === 'super-admin';

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Load chat context and health data when component mounts
  useEffect(() => {
    if (isOpen) {
      loadHealthData();
      loadChatContext();
      loadUploadedFiles();
    } else {
      // Abort stream when chat closes
      if (currentEventSource) {
        currentEventSource.abort();
        setCurrentEventSource(null);
        setStreamingMessageId(null);
      }
    }
  }, [isOpen]);


  // Cleanup stream on component unmount
  useEffect(() => {
    return () => {
      if (currentEventSource) {
        currentEventSource.abort();
      }
    };
  }, [currentEventSource]);

  const loadHealthData = async () => {
    try {
      const health = await getChatbotHealth();
      setHealthData(health);
    } catch (error) {
      console.error('Failed to load health data:', error);
    }
  };

  const loadUploadedFiles = async () => {
    try {
      const files = await getChatbotFiles();
      setUploadedFiles(files);
    } catch (error) {
      console.error('Failed to load uploaded files:', error);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    // Validate file size
    if (file.size > CHATBOT_FILE_LIMITS.MAX_FILE_SIZE) {
      const errorMsg: ChatMessage = {
        id: `file-error-${Date.now()}`,
        type: 'bot',
        message: t('chatbotInterface.fileSizeError'),
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMsg]);
      return;
    }

    // Check file limit
    if (uploadedFiles.length >= CHATBOT_FILE_LIMITS.MAX_FILES) {
      const errorMsg: ChatMessage = {
        id: `file-limit-${Date.now()}`,
        type: 'bot',
        message: t('chatbotInterface.maxFilesError'),
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMsg]);
      return;
    }

    setIsUploadingFile(true);
    setUploadingFileName(file.name);

    try {
      const response = await uploadChatbotFile(file);

      // Add the new file to the list
      setUploadedFiles(prev => [...prev, {
        file_id: response.file_id,
        filename: response.filename,
        status: response.status,
        chunks_created: response.chunks_created,
        tokens_used: response.tokens_used,
        expires_at: response.expires_at
      }]);

      // Show success message
      const successMsg: ChatMessage = {
        id: `file-success-${Date.now()}`,
        type: 'bot',
        message: t('chatbotInterface.fileUploaded', { filename: response.filename }),
        timestamp: new Date()
      };
      setMessages(prev => [...prev, successMsg]);

      // Refresh health data to update quota
      await loadHealthData();
    } catch (error) {
      console.error('Failed to upload file:', error);
      const errorMsg: ChatMessage = {
        id: `file-upload-error-${Date.now()}`,
        type: 'bot',
        message: error instanceof Error ? error.message : t('chatbotInterface.fileUploadError'),
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setIsUploadingFile(false);
      setUploadingFileName(null);
    }
  };

  const handleFileDelete = async (fileId: string) => {
    setDeletingFileId(fileId);
    try {
      await deleteChatbotFile(fileId);
      setUploadedFiles(prev => prev.filter(f => f.file_id !== fileId));
    } catch (error) {
      console.error('Failed to delete file:', error);
      const errorMsg: ChatMessage = {
        id: `file-delete-error-${Date.now()}`,
        type: 'bot',
        message: t('chatbotInterface.fileDeleteError'),
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setDeletingFileId(null);
    }
  };


  const loadChatContext = async () => {
    try {
      setIsLoading(true);
      const contextData: ChatbotContextResponse = await getChatbotContext();

      const historyMessages: ChatMessage[] = contextData.history?.map((item, index) => ({
        id: `history-${item.role}-${index}`,
        type: item.role === 'user' ? 'user' as const : 'bot' as const,
        message: item.content,
        timestamp: new Date(item.timestamp),
        intent: item.metadata?.intent || undefined
      })) || [];
      setMessages(historyMessages);

      // If no history, add a welcome message using configuration or fallback
      if (historyMessages.length === 0) {
        const welcomeMessage: ChatMessage = {
          id: 'welcome-1',
          type: 'bot',
          message: healthData?.configuration?.default_message ||
            t('chatbotInterface.subtitle') + ". " + t('chatbotInterface.welcomeTitle'),
          timestamp: new Date()
        };
        setMessages([welcomeMessage]);
      }
    } catch (error) {
      console.error('Failed to load chat context:', error);
      const errorMessage: ChatMessage = {
        id: 'error-1',
        type: 'bot',
        message: t('chatbotInterface.connectionError'),
        timestamp: new Date()
      };
      setMessages([errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteConversation = async () => {
    try {
      setIsDeleting(true);
      await clearChatbotContext();
      setMessages([]);
      const welcomeMessage: ChatMessage = {
        id: 'welcome-new',
        type: 'bot',
        message: t('chatbotInterface.conversationDeleted'),
        timestamp: new Date()
      };
      setMessages([welcomeMessage]);
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      const errorMessage: ChatMessage = {
        id: 'delete-error',
        type: 'bot',
        message: t('chatbotInterface.deleteError'),
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsDeleting(false);
    }
  };

  // Normalize markdown content for better final rendering after streaming
  const normalizeMarkdownContent = (text: string): string => {
    if (!text) return text;
    let normalized = text.replace(/\r\n/g, '\n');
    // Trim trailing spaces before newlines
    normalized = normalized.replace(/[ \t]+\n/g, '\n');
    // Collapse excessive blank lines to two
    normalized = normalized.replace(/\n{3,}/g, '\n\n');
    // Ensure headings end with a newline
    normalized = normalized.replace(/(^|\n)(#{1,6} .+)(?!\n)/g, '$1$2\n');
    // Ensure fenced code blocks have surrounding newlines
    normalized = normalized.replace(/```(\w+)?\n([\s\S]*?)```/g, (m) => `\n${m}\n`);
    // Add extra newline before list items if tightly coupled
    normalized = normalized.replace(/([^\n])\n([-*] )/g, '$1\n\n$2');
    return normalized.trimEnd();
  };



  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const messageToSend = inputValue.trim();
    setInputValue('');
    await sendMessageInternal(messageToSend);
  };

  const sendMessageInternal = async (messageToSend: string) => {

    // Availability and quota checks (new health schema)
    const isHealthy = healthData?.status === 'healthy';
    const isEnabled = !!healthData?.cabinet_enabled;
    const aiEnabled = !!healthData?.ai_quota?.enabled;
    const creditsRemaining = healthData?.ai_quota?.credits_remaining ?? 0;

    if (!isHealthy || !isEnabled || !aiEnabled) {
      const msg: ChatMessage = {
        id: `not-available-${Date.now()}`,
        type: 'bot',
        message: !isHealthy
          ? t('chatbotInterface.serviceUnavailable')
          : (!isEnabled
            ? t('chatbotInterface.serviceNotActive')
            : t('chatbotInterface.aiAccessDisabled')),
        timestamp: new Date()
      };
      setMessages(prev => [...prev, msg]);
      return;
    }

    if (creditsRemaining <= 0) {
      const reset = healthData?.ai_quota?.reset_time
        ? new Date(healthData.ai_quota.reset_time).toLocaleString()
        : '';
      const limit = healthData?.ai_quota?.monthly_limit;
      const quotaMessage: ChatMessage = {
        id: `quota-exhausted-${Date.now()}`,
        type: 'bot',
        message: t('chatbotInterface.quotaExhausted', { limit, reset }),
        timestamp: new Date()
      };
      setMessages(prev => [...prev, quotaMessage]);
      return;
    }

    // Check message length against configuration
    const maxLength = healthData?.configuration?.max_message_length || 1000;
    if (inputValue.trim().length > maxLength) {
      const errorMessage: ChatMessage = {
        id: `error-length-${Date.now()}`,
        type: 'bot',
        message: t('chatbotInterface.messageTooLong', { maxLength }),
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      return;
    }

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      type: 'user',
      message: messageToSend,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    setIsTyping(true);

    // Create initial bot message for streaming - but don't add it yet
    const botMessageId = `bot-${Date.now()}`;
    setStreamingMessageId(botMessageId);

    // Abort any existing stream
    if (currentEventSource) {
      currentEventSource.abort();
    }

    const resetStreamState = () => {
      setIsLoading(false);
      setIsTyping(false);
      setStreamingMessageId(null);
      setCurrentEventSource(null);
    };

    const finalizeBotMessage = async () => {
      setMessages(prev => prev.map(msg => {
        if (msg.type !== 'bot') {
          return msg;
        }

        const normalized = normalizeMarkdownContent(msg.message);
        const isCurrentMessage = msg.id === botMessageId;
        let nextMessage = msg;

        if (normalized !== msg.message) {
          nextMessage = { ...nextMessage, message: normalized };
        }

        if (isCurrentMessage && msg.toolActivity?.some(activity => activity.isActive)) {
          const updatedActivities = (msg.toolActivity || []).map(activity =>
            activity.isActive
              ? { ...activity, isActive: false, endTime: new Date() }
              : activity
          );
          nextMessage = { ...nextMessage, toolActivity: updatedActivities };
        }

        return nextMessage;
      }));

      try {
        await loadHealthData();
      } catch (error) {
        console.error('Failed to refresh health data:', error);
      }
    };

    try {
      const streamController = await sendChatMessageStream(
        messageToSend,
        (data) => {
          const content = data?.content ?? data?.response ?? data?.message;
          if (typeof content === 'string' && content.length > 0) {
            setMessages(prev => {
              if (!prev.some(msg => msg.id === botMessageId)) {
                const initialBotMessage: ChatMessage = {
                  id: botMessageId,
                  type: 'bot',
                  message: content,
                  timestamp: new Date(),
                  toolActivity: []
                };
                return [...prev, initialBotMessage];
              }

              // Append the new chunk to the existing message (accumulate)
              return prev.map(msg =>
                msg.id === botMessageId
                  ? { ...msg, message: msg.message + content }
                  : msg
              );
            });
          }
        },
        (error) => {
          console.error('Streaming error:', error);

          let errorMessage = t('chatbotInterface.responseError');

          if (error.message?.includes('network') || error.message?.includes('fetch')) {
            errorMessage = t('chatbotInterface.networkError');
          } else if (error.message?.includes('timeout')) {
            errorMessage = t('chatbotInterface.timeoutError');
          } else if (error.message?.includes('quota')) {
            errorMessage = t('chatbotInterface.dailyQuotaExhausted');
          }

          setMessages(prev => {
            if (!prev.some(msg => msg.id === botMessageId)) {
              const errorBotMessage: ChatMessage = {
                id: botMessageId,
                type: 'bot',
                message: errorMessage,
                timestamp: new Date()
              };
              return [...prev, errorBotMessage];
            }

            return prev.map(msg =>
              msg.id === botMessageId
                ? { ...msg, message: msg.message || errorMessage }
                : msg
            );
          });

          resetStreamState();
        },
        () => {
          resetStreamState();
        },
        () => {
          void finalizeBotMessage();
        },
        (tool: string, message: string) => {
          setMessages(prev => {
            if (!prev.some(msg => msg.id === botMessageId)) {
              const initialBotMessage: ChatMessage = {
                id: botMessageId,
                type: 'bot',
                message: '',
                timestamp: new Date(),
                toolActivity: [
                  {
                    tool,
                    message,
                    startTime: new Date(),
                    isActive: true
                  }
                ]
              };
              return [...prev, initialBotMessage];
            }

            return prev.map(msg => {
              if (msg.id !== botMessageId) return msg;
              const existing = msg.toolActivity || [];
              // Avoid duplicating a tool entry that is already active
              const alreadyActive = existing.some(
                a => a.tool === tool && a.isActive
              );
              if (alreadyActive) return msg;
              return {
                ...msg,
                toolActivity: [
                  ...existing,
                  {
                    tool,
                    message,
                    startTime: new Date(),
                    isActive: true
                  }
                ]
              };
            });
          });
        },
        (tool: string) => {
          setMessages(prev =>
            prev.map(msg =>
              msg.id === botMessageId
                ? {
                  ...msg,
                  toolActivity: (msg.toolActivity || []).map(activity =>
                    activity.tool === tool && activity.isActive
                      ? { ...activity, endTime: new Date(), isActive: false }
                      : activity
                  )
                }
                : msg
            )
          );
        }
      );

      setCurrentEventSource(streamController);
    } catch (error) {
      console.error('Failed to start streaming:', error);

      let errorMessage = t('chatbotInterface.generalError');

      if (!navigator.onLine) {
        errorMessage = t('chatbotInterface.noInternet');
      }

      setMessages(prev => {
        if (!prev.some(msg => msg.id === botMessageId)) {
          const errorBotMessage: ChatMessage = {
            id: botMessageId,
            type: 'bot',
            message: errorMessage,
            timestamp: new Date()
          };
          return [...prev, errorBotMessage];
        }

        return prev.map(msg =>
          msg.id === botMessageId
            ? { ...msg, message: errorMessage }
            : msg
        );
      });

      resetStreamState();
    }
  };

  const handleAudioMessage = async (audioFile: File) => {
    setShowAudioRecorder(false);
    setIsLoading(true);
    setIsTyping(true);

    const userMessage: ChatMessage = {
      id: `user-audio-${Date.now()}`,
      type: 'user',
      message: t('chatbotInterface.audioMessage'),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);

    try {
      const response = await sendAudioMessage(audioFile);

      const baseResponse = (response.message ?? response.response ?? '').trim();

      let botMessageContent = baseResponse;

      if (response.transcription) {
        botMessageContent = `**${t('chatbotInterface.transcribedText')}** "${response.transcription}"\n\n${baseResponse}`;
      }

      const botMessage: ChatMessage = {
        id: `bot-audio-${Date.now()}`,
        type: 'bot',
        message: botMessageContent,
        timestamp: new Date(),
        intent: response.intent || undefined
      };

      setMessages(prev => [...prev, botMessage]);

      // Refresh health data to update quota counters
      await loadHealthData();
    } catch (error) {
      console.error('Failed to send audio message:', error);
      const errorMessage: ChatMessage = {
        id: `error-audio-${Date.now()}`,
        type: 'bot',
        message: `${t('chatbotInterface.audioSendError')}${error instanceof Error ? error.message : 'Unknown'}`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setInputValue(suggestion);
    inputRef.current?.focus();
  };

  const handleQuickFeedback = async (messageId: string, feedbackType: FeedbackType) => {
    try {
      // Find the bot message and the previous user message
      const messageIndex = messages.findIndex(msg => msg.id === messageId);
      const botMessage = messages[messageIndex];
      const userMessage = messageIndex > 0 ? messages[messageIndex - 1] : null;

      if (!botMessage || !userMessage || botMessage.type !== 'bot' || userMessage.type !== 'user') {
        console.error('Could not find valid message pair for feedback');
        return;
      }

      await submitChatbotFeedback({
        sent_message: userMessage.message,
        received_message: botMessage.message,
        feedback_type: feedbackType
      });

      // Update the message with feedback
      setMessages(prev => prev.map(msg =>
        msg.id === messageId
          ? { ...msg, feedback: { feedback_type: feedbackType, submitted_at: new Date() } }
          : msg
      ));
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }
  };

  const handleShowDetailsDialog = (feedbackType: FeedbackType) => {
    // Find the message that was just given feedback
    const lastBotMessage = messages.filter(msg => msg.type === 'bot').pop();
    if (lastBotMessage) {
      setShowDetailsDialog({ messageId: lastBotMessage.id, feedbackType });
    }
  };

  const handleSubmitDetails = async (details: string) => {
    if (!showDetailsDialog) return;

    try {
      const { messageId, feedbackType } = showDetailsDialog;

      // Find the bot message and the previous user message
      const messageIndex = messages.findIndex(msg => msg.id === messageId);
      const botMessage = messages[messageIndex];
      const userMessage = messageIndex > 0 ? messages[messageIndex - 1] : null;

      if (!botMessage || !userMessage || botMessage.type !== 'bot' || userMessage.type !== 'user') {
        console.error('Could not find valid message pair for feedback');
        return;
      }

      await submitChatbotFeedback({
        sent_message: userMessage.message,
        received_message: botMessage.message,
        feedback_type: feedbackType,
        details
      });

      // Update the message with feedback including details
      setMessages(prev => prev.map(msg =>
        msg.id === messageId
          ? { ...msg, feedback: { feedback_type: feedbackType, details, submitted_at: new Date() } }
          : msg
      ));

    } catch (error) {
      console.error('Error submitting detailed feedback:', error);
    }
  };

  const suggestions = [
    { icon: '📅', text: t('chatbotSuggestions.myAppointments') },
    { icon: '👥', text: t('chatbotSuggestions.searchClient') },
    { icon: '📁', text: t('chatbotSuggestions.viewCases') },
    { icon: '📋', text: t('chatbotSuggestions.generalHelp') },
  ];

  const isConversationEmpty = messages.length === 0 ||
    (messages.length === 1 && messages[0].id.startsWith('welcome'));

  if (isSuperAdmin || !isOpen) return null;

  return (
    <div
      dir={i18n.dir()}
      className={cn(
        "fixed bottom-6 z-50",
        "w-[400px] max-w-[calc(100vw-2rem)] h-[600px] max-h-[calc(100vh-3rem)]",
        "bg-card",
        "rounded-2xl shadow-legal-xl",
        "border border-border/50",
        "flex flex-col overflow-hidden",
        className
      )}
      style={{ right: '1.5rem' }}
    >
      <div className="relative flex items-center justify-between px-5 py-3 border-b border-border/40 bg-muted/30">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <Avatar className="w-10 h-10 border-2 border-background shadow-sm">
            <AvatarFallback className="bg-[rgb(var(--brand-navy-rgb))] text-primary-foreground font-semibold flex items-center justify-center">
              <BotMessageSquare className="w-5 h-5 text-[rgb(var(--brand-gold-rgb))]" />
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-foreground text-sm">
              {t('chatbotInterface.title')}
            </h3>
            <p className="text-xs text-muted-foreground">
              {isTyping ? (
                <span className="flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin text-[rgb(var(--brand-navy-rgb))]" />
                  {t('chatbotInterface.typing')}
                </span>
              ) : (
                t('chatbotInterface.subtitle')
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={deleteConversation}
            disabled={isDeleting || messages.length === 0}
            className="h-8 w-8 p-0 hover:bg-destructive/10 rounded-full"
            title={t('chatbotInterface.deleteConversation')}
          >
            {isDeleting ? (
              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
            ) : (
              <Trash2 className="w-4 h-4 text-muted-foreground hover:text-destructive" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={closeChat}
            className="h-8 w-8 p-0 hover:bg-accent/50 rounded-full"
            title={t('chatbotInterface.close')}
          >
            <X className="w-4 h-4 text-muted-foreground hover:text-foreground" />
          </Button>
        </div>
      </div>

      <div className="flex-1 min-h-0 bg-muted/20">
        <ScrollArea className="h-full">
          <div className="p-4 space-y-4" dir={i18n.dir()}>
            {isLoading && messages.length === 0 ? (
              <div className="flex items-center justify-center h-full py-16">
                <div className="text-center space-y-3">
                  <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
                  <p className="text-sm text-muted-foreground">{t('loading')}...</p>
                </div>
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full py-16 text-center">
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <BotMessageSquare className="w-7 h-7 text-primary" />
                </div>
                <h4 className="font-semibold text-foreground mb-2">
                  {t('chatbotInterface.welcomeTitle')}
                </h4>
                <p className="text-sm text-muted-foreground max-w-xs mb-6">
                  {t('chatbotInterface.welcomeSubtitle')}
                </p>
              </div>
            ) : isConversationEmpty ? (
              <div className="space-y-4">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={cn(
                      "flex gap-3",
                      message.type === 'user' ? 'flex-row-reverse' : 'flex-row'
                    )}
                  >
                    <Avatar className={cn(
                      "w-8 h-8 flex-shrink-0",
                      message.type === 'bot' && "ring-2 ring-primary/20"
                    )}>
                      <AvatarFallback className={cn(
                        message.type === 'user'
                          ? "bg-muted text-foreground text-xs font-semibold"
                          : "bg-primary text-primary-foreground"
                      )}>
                        {message.type === 'user' ? (
                          user?.name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'
                        ) : (
                          <BotMessageSquare className="w-4 h-4" />
                        )}
                      </AvatarFallback>
                    </Avatar>

                    <div className={cn(
                      "flex-1 max-w-[80%] group",
                      message.type === 'user' ? 'items-end' : 'items-start'
                    )}>
                      <div className={cn(
                        "rounded-2xl px-4 py-2.5",
                        message.type === 'user'
                          ? "bg-primary text-primary-foreground"
                          : "bg-card border border-border/40"
                      )}>
                        {message.type === 'bot' ? (
                          <div className="prose prose-sm dark:prose-invert max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1" dir="auto">
                            <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: (props: any) => <a {...props} target="_blank" rel="noopener noreferrer" /> }}>
                              {message.message}
                            </ReactMarkdown>
                          </div>
                        ) : (
                          <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                            {message.message}
                          </p>
                        )}
                      </div>
                      <p className={cn(
                        "text-xs text-muted-foreground mt-1 px-1 opacity-0 group-hover:opacity-100",
                        message.type === 'user' ? 'text-right' : 'text-left'
                      )}>
                        {message.timestamp.toLocaleTimeString('fr-FR', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                ))}

                <div className="pt-4">
                  <p className="text-xs text-muted-foreground mb-3 text-center">
                    {t('chatbotInterface.suggestionsTitle')}
                  </p>
                  <div className="flex flex-wrap justify-center gap-2">
                    {suggestions.map((suggestion, index) => (
                      <button
                        key={index}
                        onClick={() => handleSuggestionClick(suggestion.text)}
                        className={cn(
                          "inline-flex items-center gap-2 px-3 py-2",
                          "bg-card border border-border/60",
                          "rounded-full text-sm text-foreground",
                          "hover:bg-primary/5 hover:border-primary/40"
                        )}
                      >
                        <span className="text-base">{suggestion.icon}</span>
                        <span>{suggestion.text}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              messages.map((message) => (
                <div
                  key={message.id}
                  className={cn(
                    "flex gap-3",
                    message.type === 'user' ? 'flex-row-reverse' : 'flex-row'
                  )}
                >
                  <Avatar className={cn(
                    "w-8 h-8 flex-shrink-0",
                    message.type === 'bot' && "ring-2 ring-primary/20"
                  )}>
                    <AvatarFallback className={cn(
                      message.type === 'user'
                        ? "bg-muted text-foreground text-xs font-semibold"
                        : "bg-primary text-primary-foreground"
                    )}>
                      {message.type === 'user' ? (
                        user?.name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'
                      ) : (
                        <BotMessageSquare className="w-4 h-4" />
                      )}
                    </AvatarFallback>
                  </Avatar>

                  <div className={cn(
                    "flex-1 max-w-[80%] group",
                    message.type === 'user' ? 'items-end' : 'items-start'
                  )}>
                    <div className={cn(
                      "rounded-2xl px-4 py-2.5",
                      message.type === 'user'
                        ? "bg-primary text-primary-foreground"
                        : "bg-card border border-border/40",
                      message.type === 'bot' && streamingMessageId === message.id && "ring-2 ring-primary/20"
                    )}>
                      {message.type === 'bot' ? (
                        <div className="space-y-2">
                          {message.toolActivity && message.toolActivity.length > 0 && (
                            <div className="space-y-1.5 rounded-xl border border-border/40 bg-muted/30 px-3 py-2">
                              <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/80">
                                <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary" />
                                نشاط الأدوات
                              </p>
                              <div className="space-y-1.5">
                                {message.toolActivity.map((activity, index) => (
                                  <div
                                    key={`${activity.tool || 'activity'}-${index}`}
                                    className="flex items-start gap-2 rounded-lg bg-background/60 px-3 py-2"
                                  >
                                    <span
                                      className={cn(
                                        "mt-1 h-2 w-2 rounded-full",
                                        activity.isActive
                                          ? "bg-amber-500 animate-pulse"
                                          : "bg-emerald-500"
                                      )}
                                    />
                                    <div className="space-y-1 text-xs">
                                      {activity.tool && activity.tool !== activity.message && (
                                        <p className="break-words font-semibold text-foreground/90">
                                          {activity.tool}
                                        </p>
                                      )}
                                      <p className="break-words text-muted-foreground/80">
                                        {activity.message}
                                      </p>
                                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground/70">
                                        {activity.isActive ? 'جارٍ التنفيذ...' : 'اكتمل'}
                                      </p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          <div className="prose prose-sm dark:prose-invert max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1" dir="auto">
                            <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: (props: any) => <a {...props} target="_blank" rel="noopener noreferrer" /> }}>
                              {message.message}
                            </ReactMarkdown>
                            {streamingMessageId === message.id && (
                              <span className="inline-block w-2 h-4 bg-primary ml-1 animate-pulse" />
                            )}
                          </div>

                          {streamingMessageId !== message.id && (() => {
                            const messageIndex = messages.findIndex(msg => msg.id === message.id);
                            const userMessage = messageIndex > 0 ? messages[messageIndex - 1] : null;

                            if (!userMessage || userMessage.type !== 'user') return null;

                            return (
                              <div className="pt-1 border-t border-border/40">
                                <FeedbackButtons
                                  messageId={message.id}
                                  userMessage={userMessage.message}
                                  botMessage={message.message}
                                  currentFeedback={message.feedback}
                                  onFeedbackSubmit={handleQuickFeedback}
                                  onShowDetailsDialog={handleShowDetailsDialog}
                                />
                              </div>
                            );
                          })()}

                          {message.feedback && (
                            <div className="text-xs text-muted-foreground flex items-center gap-1">
                              <span>✓</span>
                              <span>شكراً لتقييمك</span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                          {message.message}
                        </p>
                      )}
                    </div>
                    <p className={cn(
                      "text-xs text-muted-foreground mt-1 px-1 opacity-0 group-hover:opacity-100",
                      message.type === 'user' ? 'text-right' : 'text-left'
                    )}>
                      {message.timestamp.toLocaleTimeString('fr-FR', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
              ))
            )}

            {/* Typing Indicator */}
            {isTyping && (
              <div className="flex gap-3 animate-in slide-in-from-bottom-2 fade-in duration-300">
                <Avatar className="w-8 h-8 flex-shrink-0 shadow-legal-sm ring-2 ring-primary/20">
                  <AvatarFallback className="bg-[rgb(var(--brand-navy-rgb))] text-primary-foreground">
                    <BotMessageSquare className="w-4 h-4" />
                  </AvatarFallback>
                </Avatar>
                <div className="bg-card rounded-2xl rounded-tl-sm px-4 py-3 shadow-legal-sm border border-border/40">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-[rgb(var(--brand-navy-rgb))] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-[rgb(var(--brand-navy-rgb))] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-[rgb(var(--brand-gold-rgb))] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>
      </div>

      {/* Input Area */}
      <div className="border-t border-border/40 bg-card p-4">
        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileUpload}
          className="hidden"
          accept=".pdf,.docx,.txt,.png,.jpg,.jpeg,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,image/png,image/jpeg"
        />

        {/* Uploaded Files Display */}
        {uploadedFiles.length > 0 && (
          <div className="mb-3">
            <FileAttachment
              files={uploadedFiles}
              onDelete={handleFileDelete}
              isDeleting={deletingFileId}
            />
          </div>
        )}

        {/* Upload Progress */}
        {isUploadingFile && uploadingFileName && (
          <div className="mb-3">
            <FileUploadProgress filename={uploadingFileName} />
          </div>
        )}

        {/* Audio Recorder */}
        {showAudioRecorder && (
          <div className="mb-3 p-3 bg-primary/5 rounded-xl border border-primary/20">
            <AudioRecorder
              onAudioReady={handleAudioMessage}
              onCancel={() => setShowAudioRecorder(false)}
              disabled={isLoading}
            />
          </div>
        )}

        {/* Error Message */}
        {inputValue.length >= (healthData?.configuration?.max_message_length || 1000) && (
          <div className="mb-2 text-xs text-destructive px-1">
            تم الوصول للحد الأقصى للأحرف ({healthData?.configuration?.max_message_length || 1000} حرف)
          </div>
        )}

        {/* Input Container */}
        <div className="flex items-center justify-center gap-2">
          {/* File Upload Button */}
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={isLoading || isUploadingFile || uploadedFiles.length >= CHATBOT_FILE_LIMITS.MAX_FILES}
            variant="ghost"
            size="sm"
            className={cn(
              "h-11 w-11 p-0 rounded-full flex-shrink-0",
              "hover:bg-[rgba(var(--brand-navy-rgb),0.06)] transition-all duration-200",
              uploadedFiles.length > 0 && "bg-[rgba(var(--brand-gold-rgb),0.08)] text-[rgb(var(--brand-gold-rgb))] border border-[rgba(var(--brand-gold-rgb),0.12)]"
            )}
            title={t('chatbotInterface.attachFile')}
          >
            {isUploadingFile ? (
              <Loader2 className="w-5 h-5 animate-spin text-[rgb(var(--brand-navy-rgb))]" />
            ) : (
              <Paperclip className="w-5 h-5 text-[rgb(var(--brand-navy-rgb))]" />
            )}
          </Button>

          {/* Audio Button */}
          <Button
            onClick={() => {
              if (healthData?.audio_quota?.enabled) {
                setShowAudioRecorder(!showAudioRecorder);
              } else {
                const msg: ChatMessage = {
                  id: `audio-disabled-${Date.now()}`,
                  type: 'bot',
                  message: 'الرسائل الصوتية غير مفعلة لحسابك.',
                  timestamp: new Date()
                };
                setMessages(prev => [...prev, msg]);
              }
            }}
            disabled={isLoading}
            variant="ghost"
            size="sm"
            className={cn(
              "h-11 w-11 p-0 rounded-full flex-shrink-0",
              "hover:bg-[rgba(var(--brand-navy-rgb),0.06)] transition-all duration-200",
              showAudioRecorder && "bg-[rgba(var(--brand-gold-rgb),0.08)] text-[rgb(var(--brand-gold-rgb))] border border-[rgba(var(--brand-gold-rgb),0.12)]"
            )}
            title={
              "رسالة صوتية"
            }
          >
            <Mic className="w-5 h-5 text-[rgb(var(--brand-navy-rgb))]" />
          </Button>


          {/* Text Input */}
          <Textarea
            ref={inputRef}
            value={inputValue}
            onChange={(e) => {
              const maxLength = healthData?.configuration?.max_message_length || 1000;
              if (e.target.value.length <= maxLength) {
                setInputValue(e.target.value);
              }
            }}
            onKeyPress={handleKeyPress}
            placeholder="اطرح سؤالك القانوني..."
            disabled={isLoading}
            className={cn(
              "resize-none min-h-[80px] max-h-[200px]",
              "bg-muted/30 border-border/60",
              "focus:ring-2 focus:ring-primary/30",
              "focus:border-primary",
              "rounded-2xl text-sm leading-relaxed",
              "placeholder:text-muted-foreground",
              "transition-all duration-200",
              "shadow-legal-xs focus:shadow-legal-sm",
              inputValue.length >= (healthData?.configuration?.max_message_length || 1000) && "border-destructive focus:ring-destructive/30"
            )}
            rows={3}
            onInput={(e) => {
              const target = e.target as HTMLTextAreaElement;
              target.style.height = 'auto';
              target.style.height = Math.min(target.scrollHeight, 200) + 'px';
            }}
          />

          {/* Send Button */}
          <Button
            onClick={sendMessage}
            disabled={
              !inputValue.trim() ||
              isLoading ||
              inputValue.length > (healthData?.configuration?.max_message_length || 1000)
            }
            size="sm"
            className={cn(
              "h-11 w-11 p-0 rounded-full flex-shrink-0",
              "bg-[rgb(var(--brand-navy-rgb))]",
              "hover:bg-[rgba(var(--brand-navy-rgb),0.9)]",
              "shadow-legal-md hover:shadow-legal-lg",
              "transition-all duration-200 hover:scale-105 active:scale-95",
              "disabled:opacity-50 disabled:cursor-not-allowed",
              "disabled:hover:scale-100"
            )}
            title="إرسال"
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin text-[rgb(var(--brand-gold-rgb))]" />
            ) : (
              <Send className="w-5 h-5 text-[rgb(var(--brand-gold-rgb))]" />
            )}
          </Button>
        </div>
      </div>

      {/* Feedback Details Dialog */}
      <FeedbackDetailsDialog
        isOpen={!!showDetailsDialog}
        feedbackType={showDetailsDialog?.feedbackType || null}
        onSubmitDetails={handleSubmitDetails}
        onClose={() => setShowDetailsDialog(null)}
      />
    </div>
  );
};
